import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Sowfund vs Devotion Fund: Similar Fee, One Big Difference (Tax-Deductible or Not)';
const DESCRIPTION =
    "Sowfund and Devotion Fund both land near 5% (Devotion Fund is 5% on bank transfer, 7% on card) — but Sowfund is a 501(c)(3) nonprofit, so gifts through it are tax-deductible, and gifts through us aren't. An honest look at what that structural difference actually means for you.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover6.jpg',
            datePublished: '2026-08-12',
            dateModified: '2026-08-26',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/sowfund-vs-devotion-fund',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is Sowfund a nonprofit?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. Sowfund states it is operated by Marked Label Inc., \"a registered tax-exempt 501(c)(3) nonprofit organization\" (EIN 99-2972766), and that donations through it are tax-deductible to the extent allowed by U.S. law.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Does Sowfund charge the same fee as Devotion Fund?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Close to it. Sowfund charges a 5% service fee plus 2.2%+30¢ card processing (roughly 7%+ all-in on a card gift), close to Devotion Fund\'s 7% card rate (5% on bank transfer). Sowfund also lets donors opt to cover both fees at checkout — it says 85% do — which Devotion Fund doesn\'t offer yet. The fee isn\'t the main differentiator between the two platforms — the tax structure is.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I use Sowfund instead of Devotion Fund?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "If a meaningful share of your supporters itemize deductions and tax-deductibility will materially grow your support base, Sowfund's nonprofit structure is worth it even at the same fee. If your support is mostly smaller recurring gifts from supporters who take the standard deduction anyway, the deduction buys them little and a direct-support platform is simpler.",
                    },
                },
            ],
        },
    ],
};

export default function SowfundVsDevotionFund() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/sowfund-vs-devotion-fund' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-sowfund-vs-df-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover6.jpg" alt="A ledger and a hand-written receipt, symbolizing the paperwork behind tax-deductible giving" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>We charge 5% on bank transfers, 7% on card. Sowfund's headline is 5%. When two platforms land on a similar number, the fee stops being the interesting question — the structure underneath it is. I checked Sowfund's own site this week, and the real difference isn't price, it's what kind of organization is standing between your donor and their tax return.</p>

                    <h2>Sowfund is a 501(c)(3). We're not.</h2>

                    <p>Sowfund's site is direct about this: it's "operated by Marked Label Inc. A registered tax-exempt 501(c)(3) nonprofit organization" (EIN 99-2972766), and it states plainly that "all donations are 100% tax deductible" to the extent U.S. law allows. That's a real, verifiable nonprofit structure — not a marketing claim.</p>

                    <p>Devotion Fund is the opposite model on purpose: donations go directly to your bank account as a personal gift, the same way most recurring-support and crowdfunding platforms work. We've written about this tradeoff in detail in <a href="/resources/is-missionary-support-tax-deductible">our tax-deductibility guide</a> — the short version is that a gift to an individual, even a missionary, isn't deductible under U.S. law unless a 501(c)(3) sits in the middle of the transaction and retains discretion over the funds. Sowfund is built to be that 501(c)(3). We aren't.</p>

                    <h2>The fee is almost a coincidence</h2>

                    <p>Sowfund's own <a href="https://sowfund.org/platform-fees" target="_blank" rel="noopener noreferrer">fee page</a> now itemizes the math: a 5% service fee plus card processing of "2.2% + 30¢" (add 1% for Amex, 1.5% for international cards), or 0.8% capped at $5 for ACH bank transfers, or a flat $3 per physical check. Stacked together, a standard-card gift through Sowfund runs a little over 7% all-in — close enough to our 7% card rate that the fee still isn't the real differentiator.</p>

                    <p>One thing Sowfund does that we don't yet: it lets the donor opt to cover both fees at checkout so the missionary receives the full gift amount, and Sowfund says 85% of its donors choose to. Devotion Fund only has an informational fee breakdown in the donation dialog today, no "cover the fee" checkbox — a real gap we're tracking, not a hidden one.</p>

                    <p>Because the all-in fee is roughly a wash, this isn't a "which platform is cheaper" comparison — it's a "which structure fits your support base" comparison.</p>

                    <h2>Where Sowfund is the right call</h2>
                    <ul>
                        <li><strong>A meaningful share of your supporters itemize</strong> and would actually claim the deduction — for them, tax-deductibility is real money, not a nice-to-have.</li>
                        <li><strong>You want your platform itself to carry 501(c)(3) status</strong> rather than partnering with a separate denominational agency or fiscal sponsor for that.</li>
                        <li><strong>Your donors ask about deductibility often</strong> and you'd rather answer "yes" than explain the nonprofit-routing nuance every time.</li>
                    </ul>

                    <h2>Where Devotion Fund is the right call</h2>
                    <ul>
                        <li><strong>Most of your supporters take the standard deduction anyway</strong> — for the majority of individual U.S. donors post-2017 tax law, a charitable deduction changes nothing on their return, so the nonprofit structure buys them little.</li>
                        <li><strong>You want the simplest possible chain from gift to your bank account</strong>, with no organizational discretion layer between the two.</li>
                        <li><strong>Recurring-first support raising is the whole product you need</strong> — donor accounts, an AI-assisted prayer-letter draft, and a donor CRM built specifically around ongoing personal support, at one published rate (5% bank transfer, 7% card) with processing absorbed.</li>
                    </ul>

                    <p>If tax-deductibility is what your support base actually needs, go get it — <a href="https://sowfund.org" target="_blank" rel="noopener noreferrer">Sowfund</a> or a denominational sending agency are the honest paths there, and we'd rather you land on the right platform than stay with us for the wrong reason. If direct, recurring-first support with a simple chain from donor to your bank account is what you need, <a href="/">start a page on Devotion Fund</a>.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is Sowfund a nonprofit?</strong><br />
                    Yes. Sowfund states it is operated by Marked Label Inc., "a registered tax-exempt 501(c)(3) nonprofit organization" (EIN 99-2972766), and that donations through it are tax-deductible to the extent allowed by U.S. law.</p>

                    <p><strong>Does Sowfund charge the same fee as Devotion Fund?</strong><br />
                    Close to it. Sowfund charges a 5% service fee plus 2.2%+30¢ card processing (roughly 7%+ all-in on a card gift), close to our own 7% card rate (5% on bank transfer). Sowfund also lets donors opt to cover both fees at checkout — it says 85% do — which we don't offer yet. The fee isn't the main differentiator between the two platforms — the tax structure is.</p>

                    <p><strong>Should I use Sowfund instead of Devotion Fund?</strong><br />
                    If a meaningful share of your supporters itemize deductions and tax-deductibility will materially grow your support base, Sowfund's nonprofit structure is worth it even at the same fee. If your support is mostly smaller recurring gifts from supporters who take the standard deduction anyway, the deduction buys them little and a direct-support platform is simpler.</p>

                    <hr />
                    <p><small><em>Nonprofit status, fee, and feature claims checked against sowfund.org and sowfund.org/platform-fees directly in August 2026. Where Sowfund's own site doesn't publish a detail (like exact payout mechanics), we've said so rather than guessed. We update this post if Sowfund changes its structure or publishes more detail.</em></small></p>

                    <h2>About the author</h2>
                    <p>We're the team behind Devotion Fund — a recurring-first giving platform built for missionaries and ministry workers.</p>
                    <p><small><em>Last updated: 2026-08-26.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/sowfund-vs-devotion-fund" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
